import { Link } from 'react-router-dom';
import { ArrowRight, Mail, Phone } from 'lucide-react';
import {
  BOQ_STATUS, INSTALLATION_STATUS, PRICING_STATUS, QUOTE_STATUS, SURVEY_STATUS, dateTimeLabel,
  type BoqStatus, type PricingStatus, type QuoteStatus, type SurveyStatus,
} from '@adisys/shared';
import { Fact, MetaBadge, Section, inr2, qtyLabel, type TabProps } from '../../../lib/crm';
import { cx } from '../../../components/ui';

function SummaryCard({ title, tab, children }: { title: string; tab: string; children: React.ReactNode }) {
  return (
    <Link to={`?tab=${tab}`} replace
      className="group flex flex-col rounded-lg bg-card p-3.5 ring-1 ring-line transition-shadow hover:shadow-raised">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-ink-500">{title}</h4>
        <ArrowRight className="h-3.5 w-3.5 text-ink-300 group-hover:text-ink-600" aria-hidden />
      </div>
      <div className="space-y-1 text-sm text-ink-800">{children}</div>
    </Link>
  );
}

const Pending = ({ children }: { children: React.ReactNode }) => <p className="text-ink-400">{children}</p>;

function Progress({ label, value, total, tone }: { label: string; value: number; total: number; tone: string }) {
  const pct = total > 0 ? Math.min(100, Math.round((value / total) * 100)) : 0;
  return (
    <div>
      <div className="mb-1 flex justify-between text-xs text-ink-600">
        <span>{label}</span><span className="tabular">{qtyLabel(value)} of {qtyLabel(total)} ({pct}%)</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-ink-100">
        <div className={cx('h-full rounded-full', tone)} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function OverviewTab({ project }: TabProps) {
  const s = project.summary ?? {};
  const live = (s.quotations ?? []).find((q: any) => !['superseded', 'rejected'].includes(q.status)) ?? s.quotations?.[0];
  const order = s.order;
  const billing = s.billing;
  const inst = INSTALLATION_STATUS.byValue[project.installationStatus as 'not_started'];
  const outstanding = Number(billing?.billed ?? 0) - Number(billing?.paid ?? 0);

  return (
    <div className="space-y-6">
      <Section title="Where things stand" description="Select a card to open that stage.">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          <SummaryCard title="Site survey" tab="survey">
            {s.survey ? (
              <>
                <MetaBadge meta={SURVEY_STATUS} value={s.survey.status as SurveyStatus} />
                <p className="text-xs text-ink-500">{s.survey.assignedToName} · {dateTimeLabel(s.survey.scheduledAt)}</p>
              </>
            ) : <Pending>No survey scheduled</Pending>}
          </SummaryCard>

          <SummaryCard title="BOQ" tab="boq">
            {s.boq ? (
              <>
                <MetaBadge meta={BOQ_STATUS} value={s.boq.status as BoqStatus} />
                <p className="text-xs text-ink-500">Version {s.boq.version} · {s.boq.itemCount} line{s.boq.itemCount === 1 ? '' : 's'}</p>
              </>
            ) : <Pending>Not started</Pending>}
          </SummaryCard>

          <SummaryCard title="Pricing" tab="pricing">
            {s.pricing ? (
              <>
                <MetaBadge meta={PRICING_STATUS} value={s.pricing.status as PricingStatus} />
                {s.pricing.grandTotal != null
                  ? <p className="tabular text-xs text-ink-500">Total {inr2(s.pricing.grandTotal)} incl. GST</p>
                  : <p className="text-xs text-ink-500">Figures are released to Sales once approved</p>}
              </>
            ) : <Pending>Awaiting an approved BOQ</Pending>}
          </SummaryCard>

          <SummaryCard title="Quotation" tab="quotations">
            {live ? (
              <>
                <MetaBadge meta={QUOTE_STATUS} value={live.status as QuoteStatus} />
                <p className="tabular text-xs text-ink-500">{live.quoteNo} · {inr2(live.grandTotal)}{live.locked ? ' · locked' : ''}</p>
              </>
            ) : <Pending>No quotation yet</Pending>}
          </SummaryCard>

          <SummaryCard title="Order & procurement" tab="procurement">
            {order ? (
              <>
                <p className="font-medium">{order.orderNo}</p>
                <p className="tabular text-xs text-ink-500">{inr2(order.grandTotal)}</p>
              </>
            ) : <Pending>Starts when the customer approves</Pending>}
          </SummaryCard>

          <SummaryCard title="Billing" tab="finance">
            {billing && Number(billing.billed) > 0 ? (
              <>
                <p className="tabular">Billed {inr2(billing.billed)}</p>
                <p className={cx('tabular text-xs', outstanding > 0 ? 'text-warning' : 'text-success')}>
                  Collected {inr2(billing.paid)}{outstanding > 0 ? ` · ${inr2(outstanding)} outstanding` : ' · settled'}
                </p>
              </>
            ) : <Pending>Nothing invoiced yet</Pending>}
          </SummaryCard>
        </div>
      </Section>

      {order && Number(order.units) > 0 && (
        <Section title="Fulfilment" description="Goods secured (in stock or received) and delivered against the approved order.">
          <div className="space-y-3 rounded-lg bg-sunken/60 p-3.5">
            <Progress label="Secured for this order" value={Number(order.secured)} total={Number(order.units)} tone="bg-info" />
            <Progress label="Delivered to the customer" value={Number(order.delivered)} total={Number(order.units)} tone="bg-success" />
            {billing && Number(billing.billed) > 0 && (
              <Progress label="Collected against billed" value={Number(billing.paid)} total={Number(billing.billed)} tone="bg-brand-500" />
            )}
          </div>
        </Section>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        <Section title="Customer">
          <dl className="grid gap-3 sm:grid-cols-2">
            <Fact label="Company">{project.customerName}</Fact>
            <Fact label="Contact">{project.contactPerson}</Fact>
            <Fact label="Mobile">
              {project.customerMobile && (
                <a className="inline-flex items-center gap-1 text-brand-600 hover:underline" href={`tel:${project.customerMobile}`}>
                  <Phone className="h-3.5 w-3.5" aria-hidden />{project.customerMobile}
                </a>
              )}
            </Fact>
            <Fact label="Email">
              {project.customerEmail && (
                <a className="inline-flex items-center gap-1 break-all text-brand-600 hover:underline" href={`mailto:${project.customerEmail}`}>
                  <Mail className="h-3.5 w-3.5" aria-hidden />{project.customerEmail}
                </a>
              )}
            </Fact>
          </dl>
        </Section>

        <Section title="Project">
          <dl className="grid gap-3 sm:grid-cols-2">
            <Fact label="Site address">{project.siteAddress}</Fact>
            <Fact label="Solutions">
              {project.solutions?.length ? (
                <span className="flex flex-wrap gap-1">
                  {project.solutions.map((x: string) => <span key={x} className="rounded-full bg-ink-100 px-2 py-0.5 text-xs text-ink-700">{x}</span>)}
                </span>
              ) : null}
            </Fact>
            <Fact label="Installation">
              {inst && <span>{inst.label}{project.installationNote ? ` — ${project.installationNote}` : ''}</span>}
            </Fact>
            <Fact label="Handover">
              {project.handoverAt ? `${dateTimeLabel(project.handoverAt)}${project.handoverNote ? ` — ${project.handoverNote}` : ''}` : null}
            </Fact>
          </dl>
        </Section>
      </div>
    </div>
  );
}
